import React, { useState, useEffect, useCallback, memo, useMemo, useRef } from 'react';
import { Box, TextField, Button, Autocomplete, Snackbar, Alert, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, LinearProgress, IconButton, Tooltip, Skeleton } from '@mui/material';
import axios from 'axios';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';

interface User {
  id: number;
  username: string;
  pharmacyNum: string;
}

interface TableField {
  id: number;
  cName: string;
  cType: string;
  displayName: string;
  InputBlock: number;
  formula: string;
  sortByGroup: string;
  important: number;
  statisticsHide: number;
  hideInStatTable: number;
}

interface EditableData {
  [key: string]: string | number;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info' | 'warning';
}

const getDaysInMonth = (year: number, month: number) => {
  const date = new Date(year, month, 0);
  return date.getDate();
};

// Оптимизированная ячейка с минимальными ререндерами
const formatNumberWithSpaces = (value: string | number, cType?: string) => {
  if (!cType) return value;
  const type = cType.toLowerCase();
  if (type === 'int' || type === 'float') {
    if (value === '' || value === null || value === undefined) {
      return type === 'float' ? '0.00' : '0';
    }
    const num = typeof value === 'number' ? value : Number((value || '').toString().replace(/\s/g, '').replace(',', '.'));
    if (isNaN(num)) return type === 'float' ? '0.00' : '0';
    const parts = num.toFixed(type === 'float' ? 2 : 0).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return parts.join(type === 'float' ? ',' : '');
  }
  return value;
};

const EditableCell = memo(({
  value,
  onChange,
  onBlur,
  disabled,
  fieldName,
  rowIndex,
  cType
}: {
  value: string | number;
  onChange: (val: string) => void;
  onBlur: () => void;
  disabled: boolean;
  fieldName: string;
  rowIndex: number;
  cType?: string;
}) => {
  const [localValue, setLocalValue] = useState(() => {
    if (cType && (cType.toLowerCase() === 'int' || cType.toLowerCase() === 'float')) {
      return formatNumberWithSpaces(value, cType);
    }
    return value;
  });
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (cType && (cType.toLowerCase() === 'int' || cType.toLowerCase() === 'float')) {
      setLocalValue(formatNumberWithSpaces(value, cType));
    } else {
    setLocalValue(value);
    }
  }, [value, cType]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value;
    if (cType && (cType.toLowerCase() === 'int' || cType.toLowerCase() === 'float')) {
      // Удаляем все пробелы для внутреннего значения
      val = val.replace(/\s/g, '');
      // Форматируем обратно с пробелами
      const num = Number(val.replace(',', '.'));
      if (!isNaN(num)) {
        setLocalValue(formatNumberWithSpaces(num, cType));
      } else {
        setLocalValue(val);
      }
    } else {
      setLocalValue(val);
    }
  }, [cType]);

  const handleBlur = useCallback(() => {
    let val = localValue;
    if (cType && (cType.toLowerCase() === 'int' || cType.toLowerCase() === 'float')) {
      val = (localValue || '').toString().replace(/\s/g, '');
    }
    // Сравниваем очищённое значение с исходным value
    let cleanValue = value;
    if (cType && (cType.toLowerCase() === 'int' || cType.toLowerCase() === 'float')) {
      cleanValue = (value || '').toString().replace(/\s/g, '');
    }
    if (val !== cleanValue) {
      onChange(val as string);
    }
    onBlur();
  }, [localValue, onChange, onBlur, cType, value]);

  return (
    <TextField
      inputRef={inputRef}
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
      disabled={disabled}
      size="small"
      variant="outlined"
      InputProps={{
        style: { fontSize: '14px' }
      }}
      sx={{
        '& .MuiOutlinedInput-root': {
          '& fieldset': {
            borderColor: 'rgba(0, 0, 0, 0.23)',
          },
          '&:hover fieldset': {
            borderColor: 'rgba(0, 0, 0, 0.87)',
          },
          '&.Mui-focused fieldset': {
            borderColor: '#1976d2',
          },
        },
      }}
    />
  );
});

EditableCell.displayName = 'EditableCell';

// Мемоизированная строка таблицы
const TableRowMemo = memo(({ 
  row, 
  rowIndex, 
  editableFields, 
  changedCells, 
  onCellChange, 
  onCellBlur,
  formatDisplayDate 
}: {
  row: EditableData;
  rowIndex: number;
  editableFields: TableField[];
  changedCells: Set<string>;
  onCellChange: (rowIndex: number, fieldName: string, value: string, fieldType: string) => void;
  onCellBlur: (rowIndex: number) => void;
  formatDisplayDate: (dateStr: string) => string;
}) => {
  const handleCellChange = useCallback((fieldName: string, value: string, fieldType: string) => {
    onCellChange(rowIndex, fieldName, value, fieldType);
  }, [rowIndex, onCellChange]);

  const handleCellBlur = useCallback(() => {
    onCellBlur(rowIndex);
  }, [rowIndex, onCellBlur]);

  return (
    <TableRow>
      <TableCell 
        sx={{ 
          minWidth: '150px', 
          position: 'sticky', 
          left: 0, 
          background: '#f5f5f5', 
          zIndex: 1, 
          boxShadow: '2px 0 5px -2px #ccc',
          fontSize: '14px'
        }}
      >
        {formatDisplayDate(row['date'] as string)}
      </TableCell>
      {editableFields.map(field => {
        const cellKey = `${rowIndex}_${field.cName.trim()}`;
        const isChanged = changedCells.has(cellKey);
        const isDisabled = field.InputBlock === 1 || field.cName.trim() === 'pharmacyNum' || field.cName.trim() === 'date';
        
        return (
          <TableCell 
            key={field.cName.trim()}
            sx={{ 
              minWidth: '150px', 
              background: isChanged ? '#fff7c0' : undefined,
              padding: '8px'
            }}
          >
            <EditableCell
              value={row[field.cName.trim()] ?? ''}
              onChange={(val: string) => handleCellChange(field.cName.trim(), val, field.cType)}
              onBlur={handleCellBlur}
              disabled={isDisabled}
              fieldName={field.cName.trim()}
              rowIndex={rowIndex}
              cType={field.cType}
            />
          </TableCell>
        );
      })}
    </TableRow>
  );
});

TableRowMemo.displayName = 'TableRowMemo';

// Виртуализированная таблица для больших данных
const VirtualizedTable = memo(({ 
  editableData, 
  editableFields, 
  changedCells, 
  onCellChange, 
  onCellBlur,
  formatDisplayDate 
}: {
  editableData: EditableData[];
  editableFields: TableField[];
  changedCells: Set<string>;
  onCellChange: (rowIndex: number, fieldName: string, value: string, fieldType: string) => void;
  onCellBlur: (rowIndex: number) => void;
  formatDisplayDate: (dateStr: string) => string;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: Math.min(31, editableData.length) });
  
  const ROW_HEIGHT = 65;
  const HEADER_HEIGHT = 50;
  
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const scrollTop = container.scrollTop;
      const containerHeight = container.clientHeight;
      
      const start = Math.floor(scrollTop / ROW_HEIGHT);
      const end = Math.min(
        start + Math.ceil(containerHeight / ROW_HEIGHT) + 5, // 5 строк буфера
        editableData.length
      );
      
      setVisibleRange({ start, end });
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [editableData.length]);

  const visibleRows = useMemo(() => {
    return editableData.slice(visibleRange.start, visibleRange.end);
  }, [editableData, visibleRange]);

  const totalHeight = editableData.length * ROW_HEIGHT;

  return (
    <div 
      ref={containerRef}
      style={{ 
        height: '600px', 
        overflowY: 'auto',
        position: 'relative'
      }}
    >
      <TableContainer component={Paper} sx={{ height: '100%' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell 
                sx={{ 
                  minWidth: '150px', 
                  position: 'sticky', 
                  left: 0, 
                  zIndex: 3, 
                  background: '#fafafa', 
                  boxShadow: '2px 0 5px -2px #ccc',
                  height: HEADER_HEIGHT
                }}
              >
                Дата
              </TableCell>
              {editableFields.map(field => (
                <TableCell 
                  key={field.cName.trim()} 
                  sx={{ 
                    background: '#fafafa', 
                    zIndex: 2,
                    height: HEADER_HEIGHT,
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                >
                  {field.displayName}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Spacer для невидимых строк сверху */}
            {visibleRange.start > 0 && (
              <TableRow>
                <TableCell 
                  colSpan={editableFields.length + 1} 
                  sx={{ 
                    height: visibleRange.start * ROW_HEIGHT,
                    padding: 0,
                    border: 'none'
                  }} 
                />
              </TableRow>
            )}
            
            {visibleRows.map((row, index) => (
              <TableRowMemo
                key={visibleRange.start + index}
                row={row}
                rowIndex={visibleRange.start + index}
                editableFields={editableFields}
                changedCells={changedCells}
                onCellChange={onCellChange}
                onCellBlur={onCellBlur}
                formatDisplayDate={formatDisplayDate}
              />
            ))}
            
            {/* Spacer для невидимых строк снизу */}
            {visibleRange.end < editableData.length && (
              <TableRow>
                <TableCell 
                  colSpan={editableFields.length + 1} 
                  sx={{ 
                    height: (editableData.length - visibleRange.end) * ROW_HEIGHT,
                    padding: 0,
                    border: 'none'
                  }} 
                />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
});

VirtualizedTable.displayName = 'VirtualizedTable';

// Функция для форматирования даты в ДД.ММ.ГГГГ
const formatDisplayDate = (dateStr: string) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
};

// Функция для преобразования даты к формату YYYY-MM-DD для MySQL
const toMysqlDate = (dateStr: string) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Функция для нормализации даты к формату YYYY-MM-DD в локальном времени (UTC+6)
const normalizeDateKey = (date: string | Date) => {
  if (!date) return '';
  const d = new Date(date);
  // Приводим к локальному времени (UTC+6)
  d.setHours(d.getHours() + 6);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const EditMonthReportModule: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [tableFields, setTableFields] = useState<TableField[]>([]);
  const [editUser, setEditUser] = useState<User | null>(null);
  const [editMonth, setEditMonth] = useState<string>('');
  const [editableData, setEditableData] = useState<EditableData[]>([]);
  const [editableFields, setEditableFields] = useState<TableField[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const [snackbarQueue, setSnackbarQueue] = useState<SnackbarState[]>([]);
  const [changedCells, setChangedCells] = useState<Set<string>>(new Set());
  const [undoStack, setUndoStack] = useState<EditableData[][]>([]);
  const [redoStack, setRedoStack] = useState<EditableData[][]>([]);

  // Кеш для вычислений
  const formulaCache = useRef<Map<string, string>>(new Map());
  const calculationBatch = useRef<Set<number>>(new Set());

  useEffect(() => {
    document.title = 'Редактировать отчет за месяц | Zerde';
    fetchUsers();
    fetchTableStructure();
  }, []);

  const enqueueSnackbar = useCallback((snackbar: SnackbarState) => {
    setSnackbarQueue(prev => [...prev, snackbar]);
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get('https://tbl.zerdegroup.kz/api/users-with-pharmacy');
      if (response.data.success) {
        setUsers(response.data.users);
      } else {
        enqueueSnackbar({
          open: true,
          message: 'Не удалось загрузить список пользователей',
          severity: 'error',
        });
      }
    } catch (error) {
      enqueueSnackbar({
        open: true,
        message: 'Ошибка при загрузке пользователей',
        severity: 'error',
      });
    }
  }, [enqueueSnackbar]);

  const fetchTableStructure = useCallback(async () => {
    try {
      const response = await axios.get('https://tbl.zerdegroup.kz/api/table-fields');
      setTableFields(response.data);
    } catch (error) {
      enqueueSnackbar({
        open: true,
        message: 'Ошибка при получении структуры таблицы',
        severity: 'error',
      });
    }
  }, [enqueueSnackbar]);

  // Оптимизированные функции вычислений
  const safeEvaluate = useMemo(() => {
    const cache = new Map<string, number>();
    
    return (expression: string): number => {
      if (cache.has(expression)) {
        return cache.get(expression)!;
      }
      
      const sanitizedExpr = expression.replace(/\s+/g, '');
      if (!/^[0-9+\-*/().]+$/.test(sanitizedExpr)) {
        throw new Error('Недопустимое выражение');
      }
      
      try {
        const result = new Function(`return ${sanitizedExpr}`)();
        if (typeof result !== 'number' || !isFinite(result)) {
          throw new Error('Недопустимый результат');
        }
        cache.set(expression, result);
        return result;
      } catch {
        throw new Error('Ошибка вычисления');
      }
    };
  }, []);

  const evaluateFormula = useCallback((formula: string, fieldType: string, row: EditableData, prevRow?: EditableData): string => {
    const cacheKey = `${formula}_${JSON.stringify(row)}_${JSON.stringify(prevRow)}`;
    
    if (formulaCache.current.has(cacheKey)) {
      return formulaCache.current.get(cacheKey)!;
    }

    let result = '';
    
    if (formula.startsWith('{') && formula.endsWith('}')) {
      const variableName = formula.slice(1, -1);
      switch (variableName) {
        case 'pharmacyNum':
          result = row['pharmacyNum']?.toString() || '';
          break;
        case 'serverDate':
          result = row['date']?.toString() || '';
          break;
        case 'pastValueEndDay':
          result = prevRow ? (prevRow['cash_balance_end_of_day']?.toString() || '0') : '0';
          break;
        default:
          result = '';
      }
    } else {
      try {
        const fieldPattern = /(\w+)\((\d+)\)/g;
        let evaluatedFormula = formula.replace(fieldPattern, (_, fieldName) => {
          const fieldValue = row[fieldName] || '0';
          return fieldValue.toString();
        });
        result = safeEvaluate(evaluatedFormula).toString();
      } catch {
        result = '';
      }
    }
    
    formulaCache.current.set(cacheKey, result);
    return result;
  }, [safeEvaluate]);

  // Батчевый пересчет строк
  const batchRecalculate = useCallback(() => {
    if (calculationBatch.current.size === 0) return;
    
    const indicesToRecalculate = Array.from(calculationBatch.current).sort((a, b) => a - b);
    calculationBatch.current.clear();
    
    setEditableData(prevData => {
      const newData = [...prevData];
      
      for (const index of indicesToRecalculate) {
        if (index >= newData.length) continue;
        
        const row = newData[index];
        const newRow = { ...row };
        const prevRow = index > 0 ? newData[index - 1] : undefined;
        
        editableFields.forEach(field => {
          if (field.cName === 'cash_balance_start_of_day') return;
          if (field.formula && field.InputBlock === 1) {
            newRow[field.cName] = evaluateFormula(field.formula, field.cType, newRow, prevRow);
          }
        });
        
        newData[index] = newRow;
        
        // Цепной пересчет для последующих строк
        for (let i = index + 1; i < newData.length; i++) {
          newData[i]['cash_balance_start_of_day'] = newData[i - 1]['cash_balance_end_of_day'];
          const nextRow = { ...newData[i] };
          editableFields.forEach(field => {
            if (field.cName === 'cash_balance_start_of_day') return;
            if (field.formula && field.InputBlock === 1) {
              nextRow[field.cName] = evaluateFormula(field.formula, field.cType, nextRow, newData[i - 1]);
            }
          });
          newData[i] = nextRow;
        }
      }
      
      return newData;
    });
  }, [editableFields, evaluateFormula]);

  // Дебаунс для батчевого пересчета
  const debouncedBatchRecalculate = useMemo(() => {
    let timeoutId: NodeJS.Timeout;
    return () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(batchRecalculate, 16); // ~60fps
    };
  }, [batchRecalculate]);

  // Загрузка данных за месяц
  const loadMonthData = useCallback(async (user: User, month: string) => {
    setIsLoading(true);
    formulaCache.current.clear(); // Очистить кеш при загрузке новых данных
    try {
      const [year, m] = month.split('-').map(Number);
      const daysInMonth = getDaysInMonth(year, m);
      const fields = tableFields.filter(f => f.hideInStatTable === 0 || f.hideInStatTable === 1);
      setEditableFields(fields);
      const response = await axios.get('https://tbl.zerdegroup.kz/api/get-user-month-data', {
        params: {
          username: user.username,
          month: month
        }
      });
      let monthData: EditableData[] = [];
      // --- DEBUG START ---
      console.log('tableFields:', fields.map(f => ({ cName: f.cName, cType: f.cType })));
      if (response.data && Array.isArray(response.data.data) && response.data.data.length > 0) {
        console.log('first row from backend:', response.data.data[0]);
        console.log('backend row keys:', Object.keys(response.data.data[0]));
      }
      // --- DEBUG END ---
      if (response.data.success && Array.isArray(response.data.data)) {
        const dataArr = response.data.data;
        const struct = response.data.structure;
        const defaultRow = struct.reduce((acc: any, field: any) => {
          const key = field.cName.trim();
          switch (field.cType.toLowerCase()) {
            case 'float': acc[key] = '0.00'; break;
            case 'int': acc[key] = '0'; break;
            case 'datetime': acc[key] = null; break;
            default: acc[key] = '';
          }
          return acc;
        }, {});
        const dataByDate = new Map<string, EditableData>();
        dataArr.forEach((row: EditableData) => {
          const dateKey = normalizeDateKey(String(row.date));
          // Приводим числовые поля к '0' или '0.00', если они пустые/null, и убираем пробелы из ключей
          struct.forEach((field: any) => {
            const key = field.cName.trim();
            if ((field.cType.toLowerCase() === 'int' || field.cType.toLowerCase() === 'float') && (row[key] === '' || row[key] === null || row[key] === undefined)) {
              row[key] = field.cType.toLowerCase() === 'float' ? '0.00' : '0';
            }
            // Если в row есть ключ с пробелом, копируем в нормальный ключ
            if (row.hasOwnProperty(field.cName) && field.cName !== key) {
              row[key] = row[field.cName];
              delete row[field.cName];
            }
          });
          dataByDate.set(dateKey, row);
        });
        monthData = Array.from({ length: daysInMonth }, (_, i) => {
          const dateStr = `${year}-${String(m).padStart(2, '0')}-${String(i + 1).padStart(2, '0')}`;
          const monthDateKey = normalizeDateKey(String(dateStr));
          return {
            ...defaultRow,
            ...dataByDate.get(monthDateKey),
            pharmacyNum: user.pharmacyNum,
            date: dateStr
          };
        });
      } else {
        monthData = Array.from({ length: daysInMonth }, (_, i) => {
          const date = `${year}-${String(m).padStart(2, '0')}-${String(i + 1).padStart(2, '0')}`;
          const row: EditableData = {};
          fields.forEach(field => {
            if (field.cName === 'pharmacyNum') row[field.cName] = user.pharmacyNum;
            else if (field.cName === 'date') row[field.cName] = date;
            else row[field.cName] = '';
          });
          return row;
        });
      }
      
      setEditableData(monthData);
    } catch (error) {
      enqueueSnackbar({
        open: true,
        message: 'Ошибка при загрузке данных за месяц',
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  }, [tableFields, enqueueSnackbar]);

  // При изменении пользователя или месяца
  useEffect(() => {
    setEditableData([]);
    setUndoStack([]);
    setRedoStack([]);
    setChangedCells(new Set());
    calculationBatch.current.clear();
    
    if (editUser && editMonth) {
      loadMonthData(editUser, editMonth);
    }
  }, [editUser, editMonth, loadMonthData]);

  // Оптимизированное изменение ячейки
  const handleCellChange = useCallback((rowIndex: number, fieldName: string, value: string, fieldType: string) => {
    // Сохранение состояния для undo
    setUndoStack(prev => {
      const newStack = [...prev];
      if (newStack.length > 50) { // Ограничиваем размер стека
        newStack.shift();
      }
      newStack.push(editableData.map(row => ({ ...row })));
      return newStack;
    });
    
    setRedoStack([]);
    
    setEditableData(prevData => {
      const newData = [...prevData];
      const prevValue = newData[rowIndex][fieldName];
      newData[rowIndex] = { ...newData[rowIndex], [fieldName]: value };
      
      // Отметить ячейку как измененную
      setChangedCells(prev => {
        const key = `${rowIndex}_${fieldName}`;
        const next = new Set(prev);
        if (value !== prevValue) {
          next.add(key);
        } else {
          next.delete(key);
        }
        return next;
      });
      
      return newData;
    });
  }, [editableData]);

  // Оптимизированное размытие ячейки
  const handleCellBlur = useCallback((rowIndex: number) => {
    calculationBatch.current.add(rowIndex);
    debouncedBatchRecalculate();
  }, [debouncedBatchRecalculate]);

  // Сохранение данных
  const handleSave = useCallback(async () => {
    if (!editUser || !editMonth) return;
    
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    
    const dataToSave = editableData
      .filter(row => {
        const rowDate = toMysqlDate(row['date'] as string);
        return rowDate <= todayStr;
      })
      .map(row => ({ ...row, date: toMysqlDate(row['date'] as string) }));
    
    const futureRows = editableData.filter(row => {
      const rowDate = row['date'] as string;
      return rowDate > todayStr;
    });
    
    setIsSaveLoading(true);
    
    try {
      if (futureRows.length > 0) {
        enqueueSnackbar({
          open: true,
          message: `Будут сохранены только данные до ${todayStr} включительно (${dataToSave.length} дней). Будущие дни не сохраняются!`,
          severity: 'warning',
        });
      } else {
        enqueueSnackbar({
          open: true,
          message: `Сохраняются данные за ${dataToSave.length} дней.`,
          severity: 'info',
        });
      }
      
      const response = await axios.post('https://tbl.zerdegroup.kz/api/save-and-recalculate-user-month-data', {
        username: editUser.username,
        month: editMonth,
        data: dataToSave
      });
      
      if (response.data.success) {
        enqueueSnackbar({
          open: true,
          message: 'Данные успешно сохранены',
          severity: 'success',
        });
        setChangedCells(new Set());
      } else {
        throw new Error('Ошибка при сохранении данных');
      }
    } catch (error) {
      enqueueSnackbar({
        open: true,
        message: 'Ошибка при сохранении данных',
        severity: 'error',
      });
    } finally {
      setIsSaveLoading(false);
    }
  }, [editUser, editMonth, editableData, enqueueSnackbar]);

  const handleCloseSnackbar = useCallback((index: number) => {
    setSnackbarQueue(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Undo/Redo операции
  const handleUndo = useCallback(() => {
    if (undoStack.length === 0) return;
    
    setRedoStack(prev => [...prev, editableData.map(row => ({ ...row }))]);
    const prevState = undoStack[undoStack.length - 1];
    setEditableData(prevState.map(row => ({ ...row })));
    setUndoStack(prev => prev.slice(0, -1));
    setChangedCells(new Set());
  }, [undoStack, editableData]);

  const handleRedo = useCallback(() => {
    if (redoStack.length === 0) return;
    
    setUndoStack(prev => [...prev, editableData.map(row => ({ ...row }))]);
    const nextState = redoStack[redoStack.length - 1];
    setEditableData(nextState.map(row => ({ ...row })));
    setRedoStack(prev => prev.slice(0, -1));
    setChangedCells(new Set());
  }, [redoStack, editableData]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 'z') {
        e.preventDefault();
        handleUndo();
      } else if (e.ctrlKey && e.key === 'y') {
        e.preventDefault();
        handleRedo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleUndo, handleRedo]);

  const LoadingSkeleton = memo(() => (
    <Box>
      <Skeleton variant="rectangular" height={40} sx={{ mb: 1, borderRadius: 1 }} />
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: '150px' }}>
                <Skeleton width={80} />
              </TableCell>
              {Array.from({ length: 6 }).map((_, idx) => (
                <TableCell key={idx}>
                  <Skeleton width={100} />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.from({ length: 10 }).map((_, rowIdx) => (
              <TableRow key={rowIdx}>
                <TableCell sx={{ minWidth: '150px' }}>
                  <Skeleton width={80} />
                </TableCell>
                {Array.from({ length: 6 }).map((_, colIdx) => (
                  <TableCell key={colIdx}>
                    <Skeleton width={100} />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  ));

  return (
    <Box sx={{ maxWidth: '100%', overflowX: 'auto' }}>
      <Box sx={{ 
        mb: 2, 
        display: 'flex', 
        gap: '20px', 
        alignItems: 'flex-end', 
        mt: 1, 
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', gap: '20px' }}>
          <Autocomplete
            options={users}
            getOptionLabel={(option) => `${option.username} (Аптека: ${option.pharmacyNum})`}
            renderInput={(params) => <TextField {...params} label="Выберите пользователя" />}
            onChange={(_, value) => setEditUser(value)}
            value={editUser}
            size='small'
            sx={{ width: 300 }}
          />
          <TextField
            label="Месяц"
            type="month"
            InputLabelProps={{ shrink: true }}
            value={editMonth}
            size='small'
            onChange={e => setEditMonth(e.target.value)}
          />
          <Tooltip title="Отменить (Ctrl+Z)">
            <span>
              <IconButton 
                onClick={handleUndo} 
                disabled={undoStack.length === 0} 
                aria-label="Отменить (Undo)"
              >
                <UndoIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Повторить (Ctrl+Y)">
            <span>
              <IconButton 
                onClick={handleRedo} 
                disabled={redoStack.length === 0} 
                aria-label="Повторить (Redo)"
              >
                <RedoIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <Button
          variant="contained"
          color='error'
          onClick={handleSave}
          disabled={isSaveLoading || isLoading || editableData.length === 0}
          sx={{ p: 1, width: 250 }}
          aria-label="Сохранить"
        >
          {isSaveLoading ? <CircularProgress size={24} /> : 'Сохранить'}
        </Button>
      </Box>
      
      {isLoading ? (
        <LoadingSkeleton />
      ) : (
        editableData.length > 0 && (
          <Box>
            {isSaveLoading && <LinearProgress sx={{ mb: 1 }} />}
            <VirtualizedTable
              editableData={editableData}
              editableFields={editableFields}
              changedCells={changedCells}
              onCellChange={handleCellChange}
              onCellBlur={handleCellBlur}
              formatDisplayDate={formatDisplayDate}
            />
          </Box>
        )
      )}
      
      {snackbarQueue.map((snackbar, idx) => (
        <Snackbar
          key={idx}
          open={true}
          autoHideDuration={6000}
          onClose={() => handleCloseSnackbar(idx)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={{ top: `${8 + idx * 70}px !important` }}
        >
          <Alert
            onClose={() => handleCloseSnackbar(idx)}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      ))}
    </Box>
  );
};

export default EditMonthReportModule;